import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import styled, { createGlobalStyle } from "styled-components";

import Dropdown from "components/atoms/Dropdown";
import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES, ToggleIconButton } from "components/atoms/button";
import SelectBox from "components/atoms/input/SelectBox";
import {
    isMessageDocEmpty,
    legacyTextToMessageDoc,
    messageDocHasPrepMention,
    messageDocToPlainText,
    mentionCandidates,
    prependMentionToMessageDoc,
    stringifyMessageDoc,
    updateMentionCandidates,
} from "components/atoms/message";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";
import { validateTitleTextMaxLength } from "components/validations/alert";

import NoteEditor from "components/organisms/note/NoteEditor";
import NoteEditorToolbar from "components/organisms/note/NoteEditorToolbar";
import NoteAIEditModal from "components/organisms/note/NoteAIEditModal";
import NoteHistoryPanel from "components/organisms/note/NoteHistoryPanel";
import NoteOutlinePanel from "components/organisms/note/NoteOutlinePanel";
import NoteReviewComposerOverlay from "components/organisms/note/NoteReviewComposerOverlay";
import NoteReviewPanel from "components/organisms/note/NoteReviewPanel";
import NoteReviewThreadOverlay from "components/organisms/note/NoteReviewThreadOverlay";
import NoteSelectionActionMenu from "components/organisms/note/NoteSelectionActionMenu";
import { useNoteAIEditWebSocket } from "components/organisms/note/aiEditSocket";
import { useNoteReviewAIWebSocket } from "components/organisms/note/reviewAISocket";
import {
    buildAppendContent,
    buildStructuredReplacementContent,
    validateAIEditTextReplacement,
} from "components/organisms/note/selectionPayload";
import SpaceDetailPageLayout from "components/templates/SpaceDetailPageLayout";

import {
    useNote,
    useNoteAssetCreate,
    useNoteAssetDelete,
    useNoteDelete,
    useNoteHistory,
    useNoteHistoryRestore,
    useNoteReviewCreate,
    useNoteReviewMessageCreate,
    useNoteReviewReopen,
    useNoteReviewResolve,
    useNoteReviews,
    useNoteRevisionCreate,
    useNoteUpdate,
} from "hooks/queries/useNote";
import { useSelectBoxOptions } from "hooks/queries/useOptions";
import { useReferenceList } from "hooks/queries/useReference";
import { useUser } from "hooks/queries/useUser";

import { isAdminUser, isReadonlyUser } from "utils/user";
import { getReferences } from "utils/request/reference";
import { getSpaceMemberProfiles } from "utils/request/space";

const RIGHT_RAIL_WIDTH = 48;
const RIGHT_SIDEBAR_WIDTH = 320;
const RIGHT_SIDEBAR_MIN_WIDTH = 260;
const RIGHT_SIDEBAR_MAX_WIDTH = 720;
const RIGHT_SIDEBAR_AUTOFOLD_WIDTH = 280;
const HEADER_HEIGHT = 56;
const SELECTION_ACTION_MENU_WIDTH = 190;
const SELECTION_ACTION_MENU_HEIGHT = 44;
const SELECTION_ACTION_MENU_MARGIN = 8;

const NotePrintStyle = createGlobalStyle`
    @media print {
        @page {
            margin: 18mm;
        }

        body {
            background: #ffffff !important;
        }

        body * {
            visibility: hidden !important;
        }

        .note-print-scope,
        .note-print-scope * {
            visibility: visible !important;
        }

        .note-print-scope {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            min-height: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: #ffffff !important;
        }

        .note-print-scope .note-editor {
            width: 100% !important;
            max-width: none !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            font-size: 11pt !important;
            line-height: 1.75 !important;
        }
    }
`;

function hashPresenceColor(value) {
    const palette = ["#2563eb", "#db2777", "#0f8f6f", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#4f46e5"];
    const source = String(value || "0");
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) {
        hash = (hash * 31 + source.charCodeAt(index)) >>> 0;
    }
    return palette[hash % palette.length];
}

const PageLayout = styled.div`
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    background-color: var(--color-White);
`;

const Header = styled.header`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 768px;
    height: ${HEADER_HEIGHT}px;
    padding: 0 16px;
    background-color: var(--color-White);
    border-bottom: solid 1px var(--color-Line);
`;

const HeaderTitleSection = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: min(50vw, 720px);
    min-width: 160px;
    max-width: calc(100% - 480px);
    pointer-events: none;
`;

const HeaderTitleLabel = styled.label`
    position: relative;
    display: inline-grid;
    vertical-align: top;
    align-items: center;
    padding: 4px 0;
    width: auto;
    min-width: 160px;
    font-size: 1.6rem;
    font-weight: 500;
    background-color: transparent;
    border-radius: 4px;
    pointer-events: auto;

    &::after {
        content: attr(data-value) " ";
        visibility: hidden;
        white-space: pre-wrap;
        grid-area: 1 / 2;
        font: inherit;
        margin: 0;
        resize: none;
        background: none;
        appearance: none;
        border: none;
    }
`;

const HeaderTitleInput = styled.input`
    grid-area: 1 / 2;
    padding: 4px 0;
    min-width: 160px;
    font-size: 1.6rem;
    font-weight: 400;
    color: var(--color-Black);
    background: none;
    appearance: none;
    border: none;
    border-radius: 4px;
    outline: #dce1e7;
    text-align: center;
    resize: none;
    transition: all 50ms ease-in;

    &:hover {
        background-color: var(--color-Base1);
    }

    &:focus {
        background-color: var(--color-Base1);
        border: solid 1px var(--color-Outline);
    }
`;

const HeaderActions = styled.div`
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;

const HeaderDivider = styled.div`
    width: 1px;
    height: 20px;
    background-color: var(--color-Line);
`;

const PresenceStatus = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
`;

const PresenceAvatar = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid ${(props) => props.$color || "#3b82f6"};
    background-color: ${(props) => (props.$hasImage ? "transparent" : props.$color || "#3b82f6")};
    color: var(--color-White);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-size: 1rem;
    font-weight: 700;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
    filter: ${(props) => (props.$connected ? "none" : "grayscale(1)")};
`;

const PresenceAvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const MainGrid = styled.div`
    display: grid;
    grid-template-columns:
        minmax(0, 1fr)
        ${({ $rightOpen }) => ($rightOpen ? "3px" : "0px")}
        ${({ $rightOpen, $rightWidth }) => ($rightOpen ? `${$rightWidth}px` : "0px")}
        ${RIGHT_RAIL_WIDTH}px;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

const MainWorkArea = styled.main`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    background-color: var(--color-Base1);
`;

const EditorViewport = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
`;

const StatusBar = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 28px;
    padding: 0 16px;
    border-top: solid 1px var(--color-Line);
    background-color: var(--color-White);
`;

const StatusText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const RightSidebar = styled.aside`
    min-width: 0;
    height: 100%;
    overflow: hidden;
    background-color: var(--color-White);
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
    transition: ${({ $isResizing }) => ($isResizing ? "none" : "opacity 180ms ease-in")};
`;

const RightSidebarHeader = styled.div`
    display: flex;
    align-items: center;
    height: 44px;
    padding: 0 14px;
    border-bottom: solid 1px var(--color-Line);
`;

const RightSidebarBody = styled.div`
    height: calc(100% - 44px);
    overflow: auto;
`;

const ReferenceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
`;

const ReferencePanelTabs = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    padding: 12px 12px 0;
`;

const ReferencePanelTab = styled.button`
    height: 32px;
    border: solid 1px ${({ $active }) => ($active ? "var(--color-DarkKey)" : "var(--color-Line)")};
    border-radius: 4px;
    color: ${({ $active }) => ($active ? "var(--color-DarkKey)" : "var(--color-SubBlack)")};
    background-color: ${({ $active }) => ($active ? "var(--color-Base1)" : "var(--color-White)")};
    font-size: 1.3rem;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    cursor: pointer;
`;

const ReferenceItem = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 32px;
    column-gap: 10px;
    align-items: center;
    padding: 10px 12px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
`;

const ReferenceItemContent = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
`;

const ReferenceItemTitleLink = styled.a`
    display: block;
    min-width: 0;
    overflow: hidden;
    color: var(--color-SubBlack);
    font-size: 1.3rem;
    font-weight: 400;
    line-height: 1.5;
    text-overflow: ellipsis;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
        color: var(--color-DarkKey);
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 2px solid var(--color-DarkKey);
        outline-offset: 2px;
    }
`;

const ReferenceItemTitleTooltipTarget = styled.span`
    display: block;
    min-width: 0;
`;

const ReferenceItemMeta = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const ReferenceItemMetaRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
`;

const ReferenceCitationCountBadge = styled.span`
    flex: 0 0 auto;
    max-width: 72px;
    padding: 2px 7px;
    border-radius: 4px;
    color: var(--color-DarkKey);
    background-color: var(--color-Base1);
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
`;

const ReferenceItemAction = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    align-self: center;
`;

const ReferenceInsertButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    cursor: pointer;

    &:hover:not(:disabled) {
        color: var(--color-DarkKey);
        border-color: var(--color-DarkKey);
        background-color: var(--color-Base1);
    }

    &:disabled {
        color: var(--color-Grey2);
        cursor: not-allowed;
        opacity: 0.65;
    }
`;

const EmptyPanelText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    padding: 24px 12px;
`;

const ViewMenuContainer = styled.div`
    position: absolute;
    top: 8px;
    right: 0;
    width: 200px;
    background-color: var(--color-White);
    border-radius: 4px;
    border: solid 1.5px var(--color-DarkKey);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 1;
    padding: 8px 0;
`;

const SelectBoxWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
`;

const RightResizeHandle = styled.div`
    width: 3px;
    height: 100%;
    background-color: var(--color-Grey3);
    opacity: ${({ $hidden }) => ($hidden ? 0 : 0.5)};
    cursor: ${({ $hidden }) => ($hidden ? "default" : "ew-resize")};
    transition:
        background-color 160ms ease-in,
        opacity 160ms ease-in;

    &:hover {
        background-color: ${({ $hidden }) => ($hidden ? "var(--color-Grey3)" : "var(--color-Key)")};
        opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
    }
`;

const RightRail = styled.nav`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: ${RIGHT_RAIL_WIDTH}px;
    height: 100%;
    padding: 8px 4px;
    border-left: solid 1px var(--color-Line);
    background-color: var(--color-White);
`;

const RailButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 4px;
    background-color: ${({ $active }) => ($active ? "var(--color-ButtonHover4)" : "transparent")};
    cursor: pointer;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const LeftPanel = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`;

const LeftPanelSection = styled.section`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const LeftPanelSectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 12px 12px 6px 12px;
`;

const LeftPanelSectionTitle = styled(Text).attrs({
    variant: "body-lg-500",
    color: "var(--color-SubBlack)",
})``;

const TocBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
`;

function getReferenceTitle(reference) {
    const title = reference?.bib_json?.title || reference?.reference_bib_json?.title;
    if (Array.isArray(title)) return title.filter(Boolean).join(" ");
    return title || reference?.display_name || reference?.reference_display_name || "제목 없음";
}

function ReferenceItemTitle({ href, title }) {
    return (
        <ReferenceItemTitleTooltipTarget>
            <Tooltip message={title} width="100%">
                <ReferenceItemTitleLink
                    as={href ? "a" : "span"}
                    href={href || undefined}
                    target={href ? "_blank" : undefined}
                    rel={href ? "noopener noreferrer" : undefined}
                    onClick={(event) => event.stopPropagation()}
                >
                    {title}
                </ReferenceItemTitleLink>
            </Tooltip>
        </ReferenceItemTitleTooltipTarget>
    );
}

function getUserDisplayName(user) {
    return user?.current_space?.my_member_info?.name || user?.name || user?.nickname || user?.email || "Member";
}

function getUserProfileImage(user) {
    const image = user?.current_space?.my_member_info?.image || user?.profile_image || user?.image || user?.thumbnail;
    return normalizePresenceImageUrl(image);
}

function normalizePresenceImageUrl(image) {
    if (!image) return "";
    if (process.env.REACT_APP_ENV === "local" && image.startsWith("/")) {
        return `http://${process.env.REACT_APP_API_BASE_URL}${image}`;
    }
    return image;
}

function normalizeNoteAssetUrl(fileUrl) {
    if (!fileUrl) return "";
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://") || fileUrl.startsWith("blob:")) {
        return fileUrl;
    }
    if (fileUrl.startsWith("/")) {
        return `${window.location.protocol}//${process.env.REACT_APP_API_BASE_URL}${fileUrl}`;
    }
    return fileUrl;
}

function normalizePresenceParticipant(participant) {
    if (!participant) return null;
    const seed = participant.space_member_id || participant.user_id || participant.name || "0";
    return {
        ...participant,
        color: participant.color || hashPresenceColor(seed),
    };
}

function buildFallbackPresenceParticipant(user) {
    const member = user?.current_space?.my_member_info || {};
    return normalizePresenceParticipant({
        user_id: user?.id,
        space_member_id: member.id,
        name: getUserDisplayName(user),
        image: getUserProfileImage(user),
    });
}

function createSelectionActionMenuState(payload) {
    if (!payload?.selection?.hasSelection) return null;
    return {
        selection: payload.selection,
        position: {
            top: Math.max(
                SELECTION_ACTION_MENU_MARGIN,
                Math.min(
                    window.innerHeight - SELECTION_ACTION_MENU_HEIGHT - SELECTION_ACTION_MENU_MARGIN,
                    payload.top - SELECTION_ACTION_MENU_HEIGHT - 10,
                ),
            ),
            left: Math.max(
                SELECTION_ACTION_MENU_MARGIN,
                Math.min(
                    window.innerWidth - SELECTION_ACTION_MENU_WIDTH - SELECTION_ACTION_MENU_MARGIN,
                    payload.left - SELECTION_ACTION_MENU_WIDTH / 2,
                ),
            ),
        },
    };
}

function NoteDetailPage() {
    const { space_uuid, note_no } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const noteNo = Number(note_no);
    const noteQuery = useNote({ note_no: noteNo });
    const { refetch: refetchNote } = noteQuery;
    const note = noteQuery.data;
    const aiEditSocket = useNoteAIEditWebSocket({
        enabled: !!note?.note_no,
        noteNo: note?.note_no,
        spaceUuid: space_uuid,
    });
    const {
        errorMessage: aiEditErrorMessage,
        isStreaming: isAIEditStreaming,
        responseText: aiEditResponseText,
        reset: resetAIEdit,
        startRequest: startAIEditRequest,
        status: aiEditStatus,
        stopRequest: stopAIEditRequest,
    } = aiEditSocket;
    const userQuery = useUser();
    const user = useMemo(() => userQuery.data || {}, [userQuery.data]);
    const optionsQuery = useSelectBoxOptions();
    const styleList = useMemo(() => optionsQuery.data?.env_citation_style || [], [optionsQuery.data]);
    const updateNoteMutation = useNoteUpdate();
    const deleteNoteMutation = useNoteDelete();
    const assetCreateMutation = useNoteAssetCreate();
    const assetDeleteMutation = useNoteAssetDelete();
    const revisionCreateMutation = useNoteRevisionCreate();
    const historyRestoreMutation = useNoteHistoryRestore();
    const reviewCreateMutation = useNoteReviewCreate();
    const reviewMessageCreateMutation = useNoteReviewMessageCreate();
    const reviewResolveMutation = useNoteReviewResolve();
    const reviewReopenMutation = useNoteReviewReopen();
    const { setToast } = useToast();
    const editorRef = useRef(null);
    const uploadedAssetIdsRef = useRef(new Set());
    const selectionActionMenuRef = useRef(null);
    const reviewThreadOverlayRef = useRef(null);
    const outlineScrollFrameRef = useRef(null);
    const outlineScrollLockUntilRef = useRef(0);
    const titleInputRef = useRef(null);
    const [titleDraft, setTitleDraft] = useState("");
    const [titleOldValue, setTitleOldValue] = useState("");
    const [settingMenu, setSettingMenu] = useState(false);
    const [failedPresenceImages, setFailedPresenceImages] = useState({});
    const [isCollaborationConnected, setCollaborationConnected] = useState(false);
    const [presenceParticipants, setPresenceParticipants] = useState([]);
    const [noteEditor, setNoteEditor] = useState(null);
    const [outlineItems, setOutlineItems] = useState([]);
    const [activeOutlineId, setActiveOutlineId] = useState("");
    const [selectionActionMenuState, setSelectionActionMenuState] = useState(null);
    const [aiEditSession, setAIEditSession] = useState(null);
    const [aiEditPrompt, setAIEditPrompt] = useState("");
    const [aiEditPresetId, setAIEditPresetId] = useState("");
    const [aiEditPreviewMode, setAIEditPreviewMode] = useState(false);
    const [reviewComposerState, setReviewComposerState] = useState(null);
    const [reviewComposerDraft, setReviewComposerDraft] = useState("");
    const [reviewMentionCandidates, setReviewMentionCandidates] = useState([...mentionCandidates]);
    const [reviewComposerRequestAI, setReviewComposerRequestAI] = useState(false);
    const [reviewAIMessageContentById, setReviewAIMessageContentById] = useState({});
    const [reviewAIStreamingMessageIds, setReviewAIStreamingMessageIds] = useState(() => new Set());
    const [reviewReplyDraft, setReviewReplyDraft] = useState("");
    const [reviewReplyRequestAI, setReviewReplyRequestAI] = useState(false);
    const [reviewThreadOverlayPosition, setReviewThreadOverlayPosition] = useState(null);
    const [selectedReviewThreadId, setSelectedReviewThreadId] = useState(null);
    const [focusedReviewMessageId, setFocusedReviewMessageId] = useState(() => searchParams.get("message"));
    const [pendingReviewThreadId, setPendingReviewThreadId] = useState(() => searchParams.get("thread"));
    const [pendingCreatedReviewThread, setPendingCreatedReviewThread] = useState(null);
    const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
    const [rightSidebarWidth, setRightSidebarWidth] = useState(RIGHT_SIDEBAR_WIDTH);
    const [rightSidebarResizing, setRightSidebarResizing] = useState(false);
    const [activeRightPanel, setActiveRightPanel] = useState(() => searchParams.get("panel") || "references");
    const [highlightedRevisionId] = useState(() => searchParams.get("revision"));
    const historyQuery = useNoteHistory(note?.id, { enabled: activeRightPanel === "history" });
    const reviewQuery = useNoteReviews(note?.id);
    const [referencePanelTab, setReferencePanelTab] = useState("cited");
    const allReferencesQuery = useReferenceList(
        {
            citation_style: note?.citation_style,
        },
        {
            enabled: activeRightPanel === "references" && referencePanelTab === "all",
        },
    );
    const reviewAIWebSocket = useNoteReviewAIWebSocket({
        enabled: !!note?.note_no,
        noteNo: note?.note_no,
        spaceUuid: space_uuid,
        onStarted: (payload) => {
            setReviewAIStreamingMessageIds((current) => new Set(current).add(String(payload.message_id)));
            setReviewAIMessageContentById((current) => ({
                ...current,
                [String(payload.message_id)]: stringifyMessageDoc(legacyTextToMessageDoc("")),
            }));
        },
        onDelta: (payload) => {
            setReviewAIMessageContentById((current) => {
                const key = String(payload.message_id);
                const previous = current[key] || stringifyMessageDoc(legacyTextToMessageDoc(""));
                const previousText = messageDocToPlainText(previous);
                return {
                    ...current,
                    [key]: stringifyMessageDoc(legacyTextToMessageDoc(previousText + (payload.content || ""))),
                };
            });
        },
        onCompleted: (payload) => {
            setReviewAIMessageContentById((current) => ({
                ...current,
                [String(payload.message_id)]: stringifyMessageDoc(legacyTextToMessageDoc(payload.content || "")),
            }));
            setReviewAIStreamingMessageIds((current) => {
                const next = new Set(current);
                next.delete(String(payload.message_id));
                return next;
            });
            reviewQuery.refetch();
        },
        onStopped: (payload) => {
            setReviewAIMessageContentById((current) => ({
                ...current,
                [String(payload.message_id)]: stringifyMessageDoc(legacyTextToMessageDoc(payload.content || "")),
            }));
            setReviewAIStreamingMessageIds((current) => {
                const next = new Set(current);
                next.delete(String(payload.message_id));
                return next;
            });
            reviewQuery.refetch();
        },
        onError: (payload) => {
            setReviewAIStreamingMessageIds((current) => {
                const next = new Set(current);
                if (payload?.message_id) next.delete(String(payload.message_id));
                return next;
            });
            setToast(payload?.message || "AI 리뷰 생성에 실패했습니다.", "error");
            reviewQuery.refetch();
        },
    });
    const referencedItems = useMemo(() => {
        const citationMap = new Map();
        (note?.citations || []).forEach((citation) => {
            if (!citation.reference) return;
            const current = citationMap.get(citation.reference);
            if (current) {
                citationMap.set(citation.reference, {
                    ...current,
                    citation_count: (current.citation_count || 1) + 1,
                });
                return;
            }
            citationMap.set(citation.reference, { ...citation, citation_count: 1 });
        });
        return Array.from(citationMap.values()).sort((a, b) => (a.reference_no || 0) - (b.reference_no || 0));
    }, [note?.citations]);
    const referenceCitationCountMap = useMemo(() => {
        const countMap = new Map();
        (note?.citations || []).forEach((citation) => {
            if (!citation.reference) return;
            countMap.set(citation.reference, (countMap.get(citation.reference) || 0) + 1);
        });
        return countMap;
    }, [note?.citations]);
    const referenceInlineCitationTextMap = useMemo(() => {
        const textMap = new Map();
        (note?.citations || []).forEach((citation) => {
            if (!citation.reference || !citation.inline_citation_text || textMap.has(citation.reference)) return;
            textMap.set(citation.reference, citation.inline_citation_text);
        });
        return textMap;
    }, [note?.citations]);
    const allReferenceItems = useMemo(() => {
        const data = allReferencesQuery.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.results)) return data.results;
        return [];
    }, [allReferencesQuery.data]);
    const reviewThreads = useMemo(() => {
        const baseThreads = Array.isArray(reviewQuery.data) ? reviewQuery.data : [];
        const threads =
            pendingCreatedReviewThread &&
            !baseThreads.some((thread) => String(thread.id) === String(pendingCreatedReviewThread.id))
                ? [pendingCreatedReviewThread, ...baseThreads]
                : baseThreads;
        return threads.map((thread) => ({
            ...thread,
            messages: (thread.messages || []).map((message) => {
                const key = String(message.id);
                const override = reviewAIMessageContentById[key];
                const isStreaming = reviewAIStreamingMessageIds.has(key);
                if (override == null && !isStreaming) return message;
                return {
                    ...message,
                    content: override ?? message.content,
                    payload: {
                        ...(message.payload || {}),
                        ai_status: isStreaming ? "running" : message.payload?.ai_status,
                    },
                };
            }),
        }));
    }, [pendingCreatedReviewThread, reviewAIMessageContentById, reviewAIStreamingMessageIds, reviewQuery.data]);
    const selectedReviewThread = useMemo(
        () => reviewThreads.find((thread) => thread.id === selectedReviewThreadId) || null,
        [reviewThreads, selectedReviewThreadId],
    );

    useEffect(() => {
        if (!pendingCreatedReviewThread?.id || !Array.isArray(reviewQuery.data)) {
            return;
        }
        if (reviewQuery.data.some((thread) => String(thread.id) === String(pendingCreatedReviewThread.id))) {
            setPendingCreatedReviewThread(null);
        }
    }, [pendingCreatedReviewThread, reviewQuery.data]);
    const visiblePresenceParticipants = useMemo(() => {
        const normalizedParticipants = presenceParticipants.map(normalizePresenceParticipant).filter(Boolean);
        if (normalizedParticipants.length) {
            return normalizedParticipants;
        }
        const fallbackParticipant = buildFallbackPresenceParticipant(user);
        return fallbackParticipant ? [{ ...fallbackParticipant, connected: isCollaborationConnected }] : [];
    }, [isCollaborationConnected, presenceParticipants, user]);

    useEffect(() => {
        setTitleDraft(note?.display_name || "");
    }, [note?.display_name]);

    useEffect(() => {
        setFailedPresenceImages({});
    }, [presenceParticipants]);

    useEffect(() => {
        if (!Number.isInteger(noteNo) || noteNo < 1) {
            navigate("/error", { replace: true });
        }
    }, [navigate, noteNo]);

    useEffect(() => {
        let canceled = false;
        if (!space_uuid) {
            setReviewMentionCandidates([...mentionCandidates]);
            return () => {
                canceled = true;
            };
        }
        getSpaceMemberProfiles(space_uuid)
            .then((profiles) => {
                if (canceled) return;
                updateMentionCandidates(profiles);
                setReviewMentionCandidates([...mentionCandidates]);
            })
            .catch(() => {
                if (canceled) return;
                setReviewMentionCandidates([...mentionCandidates]);
            });
        return () => {
            canceled = true;
        };
    }, [space_uuid]);

    useEffect(() => {
        const closeReviewThreadOverlay = () => {
            setSelectedReviewThreadId(null);
            setReviewReplyDraft("");
            setReviewReplyRequestAI(false);
            setReviewThreadOverlayPosition(null);
        };
        const handlePointerDown = (event) => {
            if (reviewThreadOverlayRef.current?.contains(event.target)) {
                return;
            }
            if (!selectionActionMenuRef.current?.contains(event.target)) {
                setSelectionActionMenuState(null);
            }
            closeReviewThreadOverlay();
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setSelectionActionMenuState(null);
                closeReviewThreadOverlay();
            }
        };
        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const handleBackButtonClick = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate(`/${space_uuid}/note`);
    };

    const commitTitle = () => {
        const trimmedTitle = titleDraft.trim();
        if (!note?.id) return;
        if (!trimmedTitle) {
            setTitleDraft(titleOldValue || note.display_name || "");
            setToast("제목을 입력해주세요.", "warn");
            return;
        }
        if (
            !validateTitleTextMaxLength(trimmedTitle, () => {
                titleInputRef.current?.focus();
            })
        ) {
            return;
        }
        if (trimmedTitle === note.display_name) return;
        updateNoteMutation.mutate(
            {
                id: note.id,
                display_name: trimmedTitle,
            },
            {
                onSuccess: () => {
                    setToast("제목이 저장되었습니다.", "info");
                },
                onError: () => {
                    setTitleDraft(note.display_name || "");
                    setToast("제목 저장에 실패했습니다.", "error");
                },
            },
        );
    };

    const handleTitleKeyDown = (event) => {
        if (event.nativeEvent?.isComposing || event.keyCode === 229) return;
        if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
        }
        if (event.key === "Escape") {
            setTitleDraft(titleOldValue || note?.display_name || "");
            event.currentTarget.blur();
        }
    };

    const renderPresenceAvatars = () => {
        return (
            <PresenceStatus aria-label="현재 편집 중인 멤버">
                {visiblePresenceParticipants.slice(0, 6).map((participant) => {
                    const participantKey = String(
                        participant.space_member_id || participant.user_id || participant.client_id || participant.name,
                    );
                    const displayName = participant.name || "Member";
                    const imageUrl = normalizePresenceImageUrl(participant.image);
                    const hasImage = Boolean(imageUrl && !failedPresenceImages[participantKey]);
                    const fallback = String(displayName).trim().slice(0, 1).toUpperCase() || "M";
                    return (
                        <Tooltip key={participantKey} message={displayName}>
                            <PresenceAvatar
                                aria-label={displayName}
                                $color={participant.color}
                                $connected={participant.connected !== false && isCollaborationConnected}
                                $hasImage={hasImage}
                            >
                                {hasImage ? (
                                    <PresenceAvatarImg
                                        src={imageUrl}
                                        alt={displayName}
                                        onError={() =>
                                            setFailedPresenceImages((current) => ({
                                                ...current,
                                                [participantKey]: true,
                                            }))
                                        }
                                    />
                                ) : (
                                    fallback
                                )}
                            </PresenceAvatar>
                        </Tooltip>
                    );
                })}
            </PresenceStatus>
        );
    };

    const handleFavoriteClick = () => {
        if (!note?.id) return;
        updateNoteMutation.mutate({
            id: note.id,
            is_favorite: !note.is_favorite,
        });
    };

    const handleDeleteClick = () => {
        if (!note?.id) return;
        Confirm("warn", "삭제", "연구노트를 삭제하시겠습니까?", "삭제", () => {
            deleteNoteMutation.mutate(
                { id: note.id },
                {
                    onSuccess: () => {
                        setToast("성공적으로 삭제되었습니다.", "info");
                        navigate(`/${space_uuid}/note`);
                    },
                    onError: () => {
                        setToast("삭제에 실패했습니다.", "error");
                    },
                },
            );
        });
    };

    const handleRevisionCreate = useCallback(() => {
        if (!note?.id) return;
        if (revisionCreateMutation.isLoading) return;
        if (isReadonlyUser(user) && !isAdminUser(user)) return;
        revisionCreateMutation.mutate(
            {
                noteId: note.id,
                body: editorRef.current?.getRevisionPayload() || {},
            },
            {
                onSuccess: () => {
                    setToast("현재 상태가 저장되었습니다.", "info");
                },
                onError: () => {
                    setToast("현재 상태 저장에 실패했습니다.", "error");
                },
            },
        );
    }, [note?.id, revisionCreateMutation, setToast, user]);

    useEffect(() => {
        const handleSaveShortcut = (event) => {
            const isSaveShortcut = (event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === "s";
            if (!isSaveShortcut) return;
            event.preventDefault();
            if (event.repeat) return;
            handleRevisionCreate();
        };
        window.addEventListener("keydown", handleSaveShortcut);
        return () => {
            window.removeEventListener("keydown", handleSaveShortcut);
        };
    }, [handleRevisionCreate]);

    const handleRestoreRevision = (revision) => {
        if (!note?.id || !revision?.id) return;
        Confirm("warn", "복원", "선택한 시점의 내용으로 현재 문서를 복원하시겠습니까?", "복원", () => {
            const body = editorRef.current?.restoreRevisionContent(revision);
            if (!body) {
                setToast("복원할 문서 내용을 불러오지 못했습니다.", "error");
                return;
            }
            historyRestoreMutation.mutate(
                {
                    noteId: note.id,
                    revisionId: revision.id,
                    body,
                },
                {
                    onSuccess: () => {
                        setToast("선택한 시점으로 복원되었습니다.", "info");
                        historyQuery.refetch();
                    },
                    onError: () => {
                        setToast("복원에 실패했습니다.", "error");
                    },
                },
            );
        });
    };

    const handlePrint = () => {
        window.print();
    };

    const uploadNoteMedia = useCallback(
        async (file, assetType) => {
            if (!note?.id) {
                throw new Error("note is required");
            }
            const formData = new FormData();
            formData.append("file", file);
            formData.append("note", note.id);
            formData.append("asset_type", assetType);
            formData.append("content_type", file.type || "");
            const asset = await assetCreateMutation.mutateAsync(formData);
            if (asset?.id) {
                uploadedAssetIdsRef.current.add(String(asset.id));
            }
            return {
                ...asset,
                src: normalizeNoteAssetUrl(asset?.file),
            };
        },
        [assetCreateMutation, note?.id],
    );

    const handleNoteMediaUploadError = useCallback(
        (_error, assetType) => {
            setToast(`${assetType === "video" ? "비디오" : "이미지"} 업로드에 실패했습니다.`, "error");
        },
        [setToast],
    );

    const handleOpenSelectionActionMenu = useCallback((payload) => {
        setSelectionActionMenuState(createSelectionActionMenuState(payload));
    }, []);

    const closeAIEditModal = useCallback(() => {
        if (isAIEditStreaming) {
            stopAIEditRequest();
        }
        resetAIEdit();
        setAIEditSession(null);
        setAIEditPrompt("");
        setAIEditPresetId("");
        setAIEditPreviewMode(false);
    }, [isAIEditStreaming, resetAIEdit, stopAIEditRequest]);

    const handleOpenAIEditModal = useCallback(() => {
        const selectionPayload =
            selectionActionMenuState?.selection || editorRef.current?.getSelectionActionMenuPayload?.()?.selection;
        if (!selectionPayload?.hasSelection) {
            setSelectionActionMenuState(null);
            setToast("AI 편집을 시작할 선택 영역을 찾지 못했습니다.", "warn");
            return;
        }
        setAIEditSession({
            selection: selectionPayload,
            documentVersion: note?.document?.current_version ?? 0,
            cacheVersion: note?.document?.cache_version ?? 0,
        });
        setAIEditPreviewMode(false);
        setSelectionActionMenuState(null);
    }, [note?.document?.cache_version, note?.document?.current_version, selectionActionMenuState?.selection, setToast]);

    const handleAIEditPresetSelect = useCallback((preset) => {
        setAIEditPresetId(preset.id);
        setAIEditPrompt(preset.prompt);
    }, []);

    const handleAIEditRequest = useCallback(() => {
        if (!aiEditSession?.selection) return;
        const selection = aiEditSession.selection;
        const nextAttemptCount = (aiEditSession.attemptCount || 0) + 1;
        setAIEditSession((current) => (current ? { ...current, attemptCount: nextAttemptCount } : current));
        startAIEditRequest({
            actionType: aiEditPresetId || "rewrite",
            attemptCount: nextAttemptCount,
            documentPlainText: note?.document?.plain_text_content || "",
            prompt: aiEditPrompt,
            selectedMarkup: selection.selectedMarkup,
            selectedText: selection.selectedText,
            selectedTokens: selection.selectedTokens,
        });
        setAIEditPreviewMode(false);
    }, [aiEditPresetId, aiEditPrompt, aiEditSession, note?.document?.plain_text_content, startAIEditRequest]);

    const previewAIEditResult = useCallback(() => {
        if (!aiEditResponseText || isAIEditStreaming) return;
        setAIEditPreviewMode(true);
    }, [aiEditResponseText, isAIEditStreaming]);

    const confirmAIEditApply = useCallback(() => {
        if (!aiEditSession?.selection || !aiEditResponseText) return;
        const isAppendMode = aiEditPresetId === "continue";
        const validationMessage = isAppendMode
            ? ""
            : validateAIEditTextReplacement(aiEditSession.selection, aiEditResponseText);
        if (validationMessage) {
            setToast(validationMessage, "warn");
            return;
        }
        const replacementContent = isAppendMode
            ? buildAppendContent(aiEditResponseText)
            : buildStructuredReplacementContent(aiEditSession.selection, aiEditResponseText);
        const from = isAppendMode ? aiEditSession.selection.to : aiEditSession.selection.from;
        const to = isAppendMode ? aiEditSession.selection.to : aiEditSession.selection.to;
        const applied = editorRef.current?.replaceSelectionWithContent?.({
            content: replacementContent,
            from,
            to,
        });
        if (!applied) {
            setToast("AI 편집 결과를 반영할 수 없습니다. 다시 선택 후 시도해주세요.", "warn");
            return;
        }
        setToast("AI 편집 결과를 반영했습니다.", "info");
        closeAIEditModal();
        window.requestAnimationFrame(() => {
            editorRef.current?.focusEditor?.();
        });
    }, [aiEditPresetId, aiEditResponseText, aiEditSession, closeAIEditModal, setToast]);

    const handleOpenReviewComposer = useCallback(() => {
        const payload = editorRef.current?.getReviewAnchorPayload?.();
        if (!payload?.anchor || !payload?.selection?.hasSelection) {
            setSelectionActionMenuState(null);
            setToast("리뷰를 추가할 선택 영역을 찾지 못했습니다.", "warn");
            return;
        }
        setActiveRightPanel("review");
        setRightSidebarOpen(true);
        setSelectedReviewThreadId(null);
        setReviewReplyDraft("");
        setReviewReplyRequestAI(false);
        setReviewComposerDraft("");
        setReviewComposerRequestAI(false);
        setReviewComposerState({
            anchor: payload.anchor,
            selectedText: payload.selection.selectedText || payload.anchor.selected_text || "",
            position: selectionActionMenuState?.position || { top: 120, left: 120 },
        });
        setSelectionActionMenuState(null);
    }, [selectionActionMenuState?.position, setToast]);

    const closeReviewComposer = useCallback(() => {
        setReviewComposerState(null);
        setReviewComposerDraft("");
        setReviewComposerRequestAI(false);
    }, []);

    const handleCopySelection = useCallback(() => {
        void (async () => {
            const copied = await editorRef.current?.copySelectionToClipboard?.();
            setSelectionActionMenuState(null);
            if (!copied) {
                setToast("복사할 선택 영역을 찾지 못했습니다.", "warn");
            }
        })();
    }, [setToast]);

    const handleCutSelection = useCallback(() => {
        void (async () => {
            const cut = await editorRef.current?.cutSelectionToClipboard?.();
            setSelectionActionMenuState(null);
            if (!cut) {
                setToast("잘라낼 선택 영역을 찾지 못했습니다.", "warn");
            }
        })();
    }, [setToast]);

    const handleOpenCitationSuggestion = useCallback(() => {
        const opened = editorRef.current?.openCitationSuggestionAfterSelection?.();
        setSelectionActionMenuState(null);
        if (!opened) {
            setToast("인용을 추가할 선택 영역을 찾지 못했습니다.", "warn");
        }
    }, [setToast]);

    const handleCreateReview = useCallback(
        (nextContent = null) => {
            if (!note?.id || !reviewComposerState?.anchor) return;
            const content = nextContent ?? reviewComposerDraft;
            const hasPrepMention = messageDocHasPrepMention(content);
            const requestContent =
                reviewComposerRequestAI && !hasPrepMention
                    ? prependMentionToMessageDoc(content, { id: 0, display: "PREP" })
                    : content;
            if (isMessageDocEmpty(requestContent)) {
                setToast("리뷰 내용을 입력해주세요.", "warn");
                return;
            }
            reviewCreateMutation.mutate(
                {
                    noteId: note.id,
                    body: {
                        anchor: reviewComposerState.anchor,
                        content: requestContent,
                        selected_text: reviewComposerState.selectedText,
                        created_document_version: note.document?.current_version ?? 0,
                        created_cache_version: note.document?.cache_version ?? 0,
                    },
                },
                {
                    onSuccess: (thread) => {
                        setToast("리뷰가 추가되었습니다.", "info");
                        setSelectedReviewThreadId(thread?.id || null);
                        setPendingCreatedReviewThread(thread || null);
                        closeReviewComposer();
                        if (thread?.id) {
                            window.requestAnimationFrame(() => {
                                window.requestAnimationFrame(() => {
                                    setReviewThreadOverlayPosition(
                                        editorRef.current?.scrollToReviewThread?.(thread.id) || {
                                            top: 120,
                                            left: 120,
                                        },
                                    );
                                });
                            });
                        }
                        reviewQuery.refetch();
                        const aiMessage = (thread?.messages || []).find((message) => message?.payload?.role === "ai");
                        if (aiMessage?.id) {
                            reviewAIWebSocket.startRequest({
                                threadId: thread.id,
                                messageId: aiMessage.id,
                            });
                        }
                    },
                    onError: () => {
                        setToast("리뷰 추가에 실패했습니다.", "error");
                    },
                },
            );
        },
        [
            closeReviewComposer,
            note?.document?.cache_version,
            note?.document?.current_version,
            note?.id,
            reviewComposerDraft,
            reviewComposerRequestAI,
            reviewComposerState,
            reviewCreateMutation,
            reviewAIWebSocket,
            reviewQuery,
            setToast,
        ],
    );

    const handleSelectReviewThread = useCallback((thread) => {
        if (!thread?.id) {
            setSelectedReviewThreadId(null);
            setReviewReplyDraft("");
            setReviewThreadOverlayPosition(null);
            return;
        }
        setSelectedReviewThreadId(thread?.id || null);
        setReviewReplyDraft("");
        setReviewReplyRequestAI(false);
        setReviewThreadOverlayPosition(
            editorRef.current?.scrollToReviewThread?.(thread?.id) || { top: 120, left: 120 },
        );
    }, []);

    const handleSelectReviewThreadId = useCallback((threadId, position = null) => {
        setActiveRightPanel("review");
        setRightSidebarOpen(true);
        setSelectedReviewThreadId(threadId || null);
        setReviewReplyDraft("");
        setReviewReplyRequestAI(false);
        setReviewThreadOverlayPosition(position);
    }, []);

    useEffect(() => {
        if (!pendingReviewThreadId || !noteEditor || !Array.isArray(reviewQuery.data)) return;

        const thread = reviewThreads.find((item) => String(item.id) === String(pendingReviewThreadId));
        if (!thread) {
            setPendingReviewThreadId(null);
            setFocusedReviewMessageId(null);
            return;
        }

        const message = thread.messages?.find((item) => String(item.id) === String(focusedReviewMessageId));
        setActiveRightPanel("review");
        setRightSidebarOpen(true);
        setFocusedReviewMessageId(message?.id || null);
        handleSelectReviewThread(thread);
        setPendingReviewThreadId(null);
    }, [
        focusedReviewMessageId,
        handleSelectReviewThread,
        noteEditor,
        pendingReviewThreadId,
        reviewQuery.data,
        reviewThreads,
    ]);

    const handleCreateReviewReply = useCallback(
        (thread, nextContent = null, options = {}) => {
            if (!note?.id || !thread?.id) return;
            const content = nextContent ?? reviewReplyDraft;
            const hasPrepMention = messageDocHasPrepMention(content);
            const requestContent =
                Boolean(options?.requestAI) && !hasPrepMention
                    ? prependMentionToMessageDoc(content, { id: 0, display: "PREP" })
                    : content;
            if (isMessageDocEmpty(requestContent)) {
                setToast("답글 내용을 입력해주세요.", "warn");
                return;
            }
            reviewMessageCreateMutation.mutate(
                {
                    noteId: note.id,
                    threadId: thread.id,
                    body: { content: requestContent },
                },
                {
                    onSuccess: (message) => {
                        setReviewReplyDraft("");
                        setReviewReplyRequestAI(false);
                        setToast("답글이 추가되었습니다.", "info");
                        const aiMessage = message?.ai_message || (message?.payload?.role === "ai" ? message : null);
                        if (aiMessage?.id) {
                            reviewQuery.refetch().finally(() => {
                                reviewAIWebSocket.startRequest({
                                    threadId: thread.id,
                                    messageId: aiMessage.id,
                                });
                            });
                        }
                    },
                    onError: () => {
                        setToast("답글 추가에 실패했습니다.", "error");
                    },
                },
            );
        },
        [note?.id, reviewAIWebSocket, reviewMessageCreateMutation, reviewQuery, reviewReplyDraft, setToast],
    );

    const handleResolveReviewThread = useCallback(
        (thread) => {
            if (!note?.id || !thread?.id) return;
            reviewResolveMutation.mutate(
                { noteId: note.id, threadId: thread.id },
                {
                    onSuccess: () => setToast("리뷰를 해결했습니다.", "info"),
                    onError: () => setToast("리뷰 해결에 실패했습니다.", "error"),
                },
            );
        },
        [note?.id, reviewResolveMutation, setToast],
    );

    const handleReopenReviewThread = useCallback(
        (thread) => {
            if (!note?.id || !thread?.id) return;
            reviewReopenMutation.mutate(
                { noteId: note.id, threadId: thread.id },
                {
                    onSuccess: () => setToast("리뷰를 다시 열었습니다.", "info"),
                    onError: () => setToast("리뷰 다시 열기에 실패했습니다.", "error"),
                },
            );
        },
        [note?.id, reviewReopenMutation, setToast],
    );

    const selectionActionMenuActions = useMemo(
        () => ({
            copy: handleCopySelection,
            cut: isReadonlyUser(user) ? undefined : handleCutSelection,
            "citation-create": isReadonlyUser(user) ? undefined : handleOpenCitationSuggestion,
            "ai-edit": handleOpenAIEditModal,
            "review-create": handleOpenReviewComposer,
        }),
        [
            handleCopySelection,
            handleCutSelection,
            handleOpenAIEditModal,
            handleOpenCitationSuggestion,
            handleOpenReviewComposer,
            user,
        ],
    );

    const handleOutlineSelect = (item) => {
        setActiveOutlineId(item.id);
        outlineScrollLockUntilRef.current = Date.now() + 500;
        editorRef.current?.scrollToOutlineItem(item);
    };

    const handleEditorViewportScroll = () => {
        if (Date.now() < outlineScrollLockUntilRef.current) return;
        if (outlineScrollFrameRef.current) return;
        outlineScrollFrameRef.current = window.requestAnimationFrame(() => {
            outlineScrollFrameRef.current = null;
            editorRef.current?.updateActiveOutlineFromScroll();
        });
    };

    useEffect(() => {
        return () => {
            if (outlineScrollFrameRef.current) {
                window.cancelAnimationFrame(outlineScrollFrameRef.current);
            }
        };
    }, []);

    const handleInsertReferenceCitation = useCallback(
        (reference) => {
            const inserted = editorRef.current?.insertReferenceCitation?.(reference);
            if (!inserted) {
                setToast("참고문헌을 삽입할 수 없습니다. 본문을 클릭한 뒤 다시 시도해주세요.", "warn");
                return;
            }
            setToast("인용을 삽입했습니다.", "info");
        },
        [setToast],
    );

    const handleSearchReferenceSuggestions = useCallback(
        async (query) => {
            if (!space_uuid) return [];
            try {
                const data = await getReferences(space_uuid, {
                    citation_style: note?.citation_style,
                    ordering: "-created_at",
                    page: 1,
                    page_size: 8,
                    search: query?.trim() || undefined,
                });
                if (Array.isArray(data)) return data.slice(0, 8);
                if (Array.isArray(data?.results)) return data.results;
                return [];
            } catch (_error) {
                return [];
            }
        },
        [note?.citation_style, space_uuid],
    );

    const cleanupUnusedUploadedAssets = useCallback(() => {
        if (!uploadedAssetIdsRef.current.size) return;
        const usedAssetIds = editorRef.current?.getAssetIds?.() || new Set();
        const unusedAssetIds = Array.from(uploadedAssetIdsRef.current).filter((assetId) => !usedAssetIds.has(assetId));
        unusedAssetIds.forEach((assetId) => {
            uploadedAssetIdsRef.current.delete(assetId);
            assetDeleteMutation.mutate(
                { assetId },
                {
                    onError: () => {
                        uploadedAssetIdsRef.current.add(assetId);
                    },
                },
            );
        });
    }, [assetDeleteMutation]);

    const handleNoteMaterialize = useCallback(() => {
        cleanupUnusedUploadedAssets();
        refetchNote();
    }, [cleanupUnusedUploadedAssets, refetchNote]);

    const renderLeftPanel = () => (
        <LeftPanel>
            <LeftPanelSection>
                <LeftPanelSectionHeader>
                    <Icon name="list" size="18" color="var(--color-SubBlack)" />
                    <LeftPanelSectionTitle>목차</LeftPanelSectionTitle>
                </LeftPanelSectionHeader>
                <TocBody>
                    <NoteOutlinePanel
                        activeOutlineId={activeOutlineId}
                        items={outlineItems}
                        onSelect={handleOutlineSelect}
                    />
                </TocBody>
            </LeftPanelSection>
        </LeftPanel>
    );

    const renderRightPanel = () => {
        const labels = {
            references: "참고문헌",
            review: "리뷰",
            history: "히스토리",
        };
        const renderReferencesPanel = () => {
            const renderReferenceMeta = (reference) => {
                return (
                    reference.inline_citation_text ||
                    referenceInlineCitationTextMap.get(reference.reference || reference.id) ||
                    ""
                );
            };
            const renderCitedReferences = () => {
                if (!referencedItems.length) {
                    return <EmptyPanelText>인용된 참고문헌이 없습니다.</EmptyPanelText>;
                }
                return (
                    <ReferenceList>
                        {referencedItems.map((citation) => {
                            const meta = renderReferenceMeta(citation);
                            const title = getReferenceTitle(citation);
                            return (
                                <ReferenceItem key={citation.reference}>
                                    <ReferenceItemContent>
                                        <ReferenceItemTitle
                                            href={
                                                citation.reference_no
                                                    ? `/${space_uuid}/reference/${citation.reference_no}`
                                                    : ""
                                            }
                                            title={title}
                                        />
                                        <ReferenceItemMetaRow>
                                            {meta && <ReferenceItemMeta ellipsis>{meta}</ReferenceItemMeta>}
                                            <ReferenceCitationCountBadge>
                                                {citation.citation_count || 1}회 인용
                                            </ReferenceCitationCountBadge>
                                        </ReferenceItemMetaRow>
                                    </ReferenceItemContent>
                                    <ReferenceItemAction>
                                        <Tooltip message="인용 추가" position="left">
                                            <ReferenceInsertButton
                                                type="button"
                                                aria-label={`${title} 인용 추가`}
                                                disabled={isReadonlyUser(user)}
                                                onClick={() =>
                                                    handleInsertReferenceCitation({
                                                        id: citation.reference,
                                                        reference_no: citation.reference_no,
                                                    })
                                                }
                                            >
                                                <Icon name="plus" size="14" color="currentColor" />
                                            </ReferenceInsertButton>
                                        </Tooltip>
                                    </ReferenceItemAction>
                                </ReferenceItem>
                            );
                        })}
                    </ReferenceList>
                );
            };
            const renderAllReferences = () => {
                if (allReferencesQuery.isLoading) {
                    return <EmptyPanelText>참고문헌을 불러오는 중입니다.</EmptyPanelText>;
                }
                if (!allReferenceItems.length) {
                    return <EmptyPanelText>참고문헌이 없습니다.</EmptyPanelText>;
                }
                return (
                    <ReferenceList>
                        {allReferenceItems.map((reference) => {
                            const meta = renderReferenceMeta(reference);
                            const title = getReferenceTitle(reference);
                            const citationCount = referenceCitationCountMap.get(reference.id) || 0;
                            return (
                                <ReferenceItem key={reference.id}>
                                    <ReferenceItemContent>
                                        <ReferenceItemTitle
                                            href={
                                                reference.reference_no
                                                    ? `/${space_uuid}/reference/${reference.reference_no}`
                                                    : ""
                                            }
                                            title={title}
                                        />
                                        <ReferenceItemMetaRow>
                                            {meta && <ReferenceItemMeta ellipsis>{meta}</ReferenceItemMeta>}
                                            {citationCount > 0 && (
                                                <ReferenceCitationCountBadge>
                                                    {citationCount}회 인용
                                                </ReferenceCitationCountBadge>
                                            )}
                                        </ReferenceItemMetaRow>
                                    </ReferenceItemContent>
                                    <ReferenceItemAction>
                                        <Tooltip
                                            message={citationCount > 0 ? "인용 추가" : "인용 삽입"}
                                            position="left"
                                        >
                                            <ReferenceInsertButton
                                                type="button"
                                                aria-label={`${title} 인용 삽입`}
                                                disabled={isReadonlyUser(user)}
                                                onClick={() => handleInsertReferenceCitation(reference)}
                                            >
                                                <Icon name="plus" size="14" color="currentColor" />
                                            </ReferenceInsertButton>
                                        </Tooltip>
                                    </ReferenceItemAction>
                                </ReferenceItem>
                            );
                        })}
                    </ReferenceList>
                );
            };

            return (
                <>
                    <ReferencePanelTabs role="tablist" aria-label="참고문헌 보기">
                        <ReferencePanelTab
                            type="button"
                            role="tab"
                            $active={referencePanelTab === "cited"}
                            aria-selected={referencePanelTab === "cited"}
                            onClick={() => setReferencePanelTab("cited")}
                        >
                            인용
                        </ReferencePanelTab>
                        <ReferencePanelTab
                            type="button"
                            role="tab"
                            $active={referencePanelTab === "all"}
                            aria-selected={referencePanelTab === "all"}
                            onClick={() => setReferencePanelTab("all")}
                        >
                            전체
                        </ReferencePanelTab>
                    </ReferencePanelTabs>
                    {referencePanelTab === "cited" ? renderCitedReferences() : renderAllReferences()}
                </>
            );
        };

        return (
            <RightSidebar $open={rightSidebarOpen} $isResizing={rightSidebarResizing}>
                <RightSidebarHeader>
                    <Text variant="body-lg-500" color="var(--color-SubBlack)">
                        {labels[activeRightPanel]}
                    </Text>
                </RightSidebarHeader>
                <RightSidebarBody>
                    {activeRightPanel === "references" ? renderReferencesPanel() : null}
                    {activeRightPanel === "history" ? (
                        <NoteHistoryPanel
                            historyItems={historyQuery.data || []}
                            isLoading={historyQuery.isLoading}
                            onRestore={handleRestoreRevision}
                            restoreLoading={historyRestoreMutation.isLoading}
                            highlightedRevisionId={highlightedRevisionId}
                        />
                    ) : null}
                    {activeRightPanel === "review" ? (
                        <NoteReviewPanel
                            isLoading={reviewQuery.isLoading}
                            reviewThreads={reviewThreads}
                            selectedThreadId={selectedReviewThreadId}
                            onSelectThread={handleSelectReviewThread}
                        />
                    ) : null}
                </RightSidebarBody>
            </RightSidebar>
        );
    };

    const handleRightRailClick = (panel) => {
        if (activeRightPanel === panel) {
            setRightSidebarOpen((prev) => !prev);
            return;
        }
        setActiveRightPanel(panel);
        setRightSidebarOpen(true);
    };

    const startRightSidebarResizing = (event) => {
        if (!rightSidebarOpen) return;
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = rightSidebarWidth;
        let latestWidth = rightSidebarWidth;
        setRightSidebarResizing(true);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        const handleMouseMove = (moveEvent) => {
            const nextWidth = Math.max(
                RIGHT_SIDEBAR_MIN_WIDTH,
                Math.min(RIGHT_SIDEBAR_MAX_WIDTH, startWidth + startX - moveEvent.clientX),
            );
            latestWidth = nextWidth;
            setRightSidebarWidth(nextWidth);
        };

        const handleMouseUp = () => {
            setRightSidebarResizing(false);
            if (latestWidth <= RIGHT_SIDEBAR_AUTOFOLD_WIDTH) {
                setRightSidebarOpen(false);
                setRightSidebarWidth(RIGHT_SIDEBAR_WIDTH);
            }
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    return (
        <PageLayout>
            <NotePrintStyle />
            <HtmlHead title={note?.display_name || "연구노트"} />
            <Header>
                <Link to="/" reloadDocument>
                    <Image name={"headerLogo"} />
                </Link>
                <HeaderTitleSection>
                    <form onSubmit={(event) => event.preventDefault()}>
                        <HeaderTitleLabel data-value={titleDraft}>
                            <HeaderTitleInput
                                ref={titleInputRef}
                                aria-label="연구노트 제목"
                                size={2}
                                maxLength={255}
                                value={titleDraft}
                                placeholder="연구노트"
                                onFocus={() => setTitleOldValue(titleDraft)}
                                onChange={(event) => setTitleDraft(event.target.value)}
                                onKeyDown={handleTitleKeyDown}
                                onBlur={commitTitle}
                                disabled={!note || isReadonlyUser(user)}
                            />
                        </HeaderTitleLabel>
                    </form>
                </HeaderTitleSection>
                <HeaderActions>
                    {renderPresenceAvatars()}
                    <HeaderDivider />
                    <Tooltip message={"즐겨찾기"}>
                        <Button
                            {...BUTTON_STYLES.iconSurfaceStatic}
                            border={
                                note?.is_favorite
                                    ? "solid 2px var(--color-Key)"
                                    : "solid 2px var(--color-DisabledButton)"
                            }
                            onClick={handleFavoriteClick}
                            disabled={!note || isReadonlyUser(user)}
                        >
                            <Icon
                                name={"bookmark"}
                                size={"12"}
                                color={note?.is_favorite ? "var(--color-Key)" : "var(--color-DisabledButton)"}
                            />
                        </Button>
                    </Tooltip>
                    <Tooltip message={"설정"}>
                        <Dropdown
                            open={settingMenu}
                            onOpenChange={setSettingMenu}
                            placement="bottom-end"
                            offset={4}
                            anchor={
                                <ToggleIconButton
                                    aria-label="설정"
                                    active={settingMenu}
                                    variant="secondary"
                                    padding="0 4px"
                                    buttonSize="24px"
                                    renderIcon={(active) => (
                                        <Icon
                                            name={active ? "x" : "setting"}
                                            color={active ? "var(--color-DarkKey)" : "var(--color-Black)"}
                                            size={"12px"}
                                        />
                                    )}
                                    disabled={!note || isReadonlyUser(user)}
                                    onClick={() => setSettingMenu((prevState) => !prevState)}
                                />
                            }
                        >
                            <ViewMenuContainer>
                                <SelectBoxWrapper>
                                    <Text variant="body-sm-400" color={"var(--color-Black)"}>
                                        AI 응답 언어
                                    </Text>
                                    <SelectBox
                                        value={note?.ai_response_language}
                                        items={[
                                            { id: "ko", name: "한국어" },
                                            { id: "en", name: "English" },
                                        ]}
                                        onChange={(value) => {
                                            if (!note?.id) return;
                                            updateNoteMutation.mutate({
                                                id: note.id,
                                                ai_response_language: value,
                                            });
                                        }}
                                        width={"90px"}
                                    />
                                </SelectBoxWrapper>
                                <SelectBoxWrapper>
                                    <Text variant="body-sm-400" color={"var(--color-Black)"}>
                                        인용 스타일
                                    </Text>
                                    <SelectBox
                                        value={note?.citation_style}
                                        items={styleList}
                                        onChange={(value) => {
                                            if (!note?.id) return;
                                            updateNoteMutation.mutate({
                                                id: note.id,
                                                citation_style: value,
                                            });
                                        }}
                                        width={"90px"}
                                    />
                                </SelectBoxWrapper>
                            </ViewMenuContainer>
                        </Dropdown>
                    </Tooltip>
                    <Tooltip message={"연구노트 삭제"}>
                        <Button
                            onlyIcon
                            {...BUTTON_STYLES.actionPrimary}
                            onClick={handleDeleteClick}
                            disabled={!note || (isReadonlyUser(user) && !isAdminUser(user))}
                        >
                            <Icon name={"delete"} size={"12"} color={"var(--color-White)"} />
                        </Button>
                    </Tooltip>
                </HeaderActions>
            </Header>
            <SpaceDetailPageLayout
                headerHeight={HEADER_HEIGHT}
                navigate={navigate}
                onBack={handleBackButtonClick}
                sidebarContent={renderLeftPanel()}
                sidebarTitle={"상세보기"}
                sidebarTitleLabel={"연구노트"}
                spaceUuid={space_uuid}
                user={user}
            >
                <MainGrid $rightOpen={rightSidebarOpen} $rightWidth={rightSidebarWidth}>
                    <MainWorkArea>
                        <NoteEditorToolbar
                            disabled={!note || isReadonlyUser(user)}
                            editor={noteEditor}
                            isSaving={revisionCreateMutation.isLoading}
                            onPrint={handlePrint}
                            onSaveRevision={handleRevisionCreate}
                        />
                        <EditorViewport className="note-editor-viewport" onScroll={handleEditorViewportScroll}>
                            {note && (
                                <NoteEditor
                                    ref={editorRef}
                                    note={note}
                                    onConnectionChange={setCollaborationConnected}
                                    onActiveOutlineChange={setActiveOutlineId}
                                    onEditorReady={setNoteEditor}
                                    onMaterialize={handleNoteMaterialize}
                                    onOpenSelectionActionMenu={handleOpenSelectionActionMenu}
                                    onOutlineChange={setOutlineItems}
                                    onPresenceChange={setPresenceParticipants}
                                    onSelectReviewThread={handleSelectReviewThreadId}
                                    onUploadError={handleNoteMediaUploadError}
                                    reviewThreads={reviewThreads}
                                    searchReferences={handleSearchReferenceSuggestions}
                                    selectedReviewThreadId={selectedReviewThreadId}
                                    spaceUuid={space_uuid}
                                    uploadNoteMedia={uploadNoteMedia}
                                />
                            )}
                        </EditorViewport>
                        <StatusBar>
                            <StatusText>저장됨</StatusText>
                            <StatusText>revision v{note?.latest_revision_seq ?? 0}</StatusText>
                        </StatusBar>
                    </MainWorkArea>
                    <RightResizeHandle
                        $hidden={!rightSidebarOpen}
                        onMouseDown={rightSidebarOpen ? startRightSidebarResizing : undefined}
                    />
                    {renderRightPanel()}
                    <RightRail aria-label="연구노트 우측 패널">
                        <Tooltip message={rightSidebarOpen ? "우측 사이드바 닫기" : "우측 사이드바 열기"}>
                            <RailButton
                                type="button"
                                aria-label={rightSidebarOpen ? "우측 사이드바 닫기" : "우측 사이드바 열기"}
                                onClick={() => setRightSidebarOpen((prev) => !prev)}
                            >
                                <Icon
                                    name={rightSidebarOpen ? "arrowRight" : "arrowLeft"}
                                    size="12"
                                    color="var(--color-SubBlack)"
                                />
                            </RailButton>
                        </Tooltip>
                        <Tooltip message={"참고문헌"}>
                            <RailButton
                                type="button"
                                aria-label="참고문헌"
                                $active={activeRightPanel === "references"}
                                onClick={() => handleRightRailClick("references")}
                            >
                                <Icon name={"list"} size="14" color="var(--color-SubBlack)" />
                            </RailButton>
                        </Tooltip>
                        <Tooltip message={"리뷰"}>
                            <RailButton
                                type="button"
                                aria-label="리뷰"
                                $active={activeRightPanel === "review"}
                                onClick={() => handleRightRailClick("review")}
                            >
                                <Icon name={"thread"} size="14" color="var(--color-SubBlack)" />
                            </RailButton>
                        </Tooltip>
                        <Tooltip message={"히스토리"}>
                            <RailButton
                                type="button"
                                aria-label="히스토리"
                                $active={activeRightPanel === "history"}
                                onClick={() => handleRightRailClick("history")}
                            >
                                <Icon name={"last"} size="14" color="var(--color-SubBlack)" />
                            </RailButton>
                        </Tooltip>
                    </RightRail>
                </MainGrid>
            </SpaceDetailPageLayout>
            {selectionActionMenuState
                ? createPortal(
                      <NoteSelectionActionMenu
                          actions={selectionActionMenuActions}
                          menuRef={selectionActionMenuRef}
                          position={selectionActionMenuState.position}
                      />,
                      document.body,
                  )
                : null}
            {reviewComposerState
                ? createPortal(
                      <NoteReviewComposerOverlay
                          draft={reviewComposerDraft}
                          isLoading={reviewCreateMutation.isLoading}
                          mentions={{ enabled: true, data: reviewMentionCandidates }}
                          position={reviewComposerState.position}
                          requestAI={reviewComposerRequestAI}
                          selectedText={reviewComposerState.selectedText}
                          onChange={setReviewComposerDraft}
                          onClose={closeReviewComposer}
                          onRequestAIChange={setReviewComposerRequestAI}
                          onSubmit={handleCreateReview}
                      />,
                      document.body,
                  )
                : null}
            {selectedReviewThread && reviewThreadOverlayPosition
                ? createPortal(
                      <NoteReviewThreadOverlay
                          mentions={{ enabled: true, data: reviewMentionCandidates }}
                          overlayRef={reviewThreadOverlayRef}
                          position={reviewThreadOverlayPosition}
                          replyDraft={reviewReplyDraft}
                          replyLoading={reviewMessageCreateMutation.isLoading}
                          replyRequestAI={reviewReplyRequestAI}
                          setReplyDraft={setReviewReplyDraft}
                          setReplyRequestAI={setReviewReplyRequestAI}
                          thread={selectedReviewThread}
                          focusedMessageId={focusedReviewMessageId}
                          onClose={() => {
                              setSelectedReviewThreadId(null);
                              setReviewReplyDraft("");
                              setReviewReplyRequestAI(false);
                              setReviewThreadOverlayPosition(null);
                          }}
                          onReopen={handleReopenReviewThread}
                          onReply={handleCreateReviewReply}
                          onResolve={handleResolveReviewThread}
                      />,
                      document.body,
                  )
                : null}
            {aiEditSession
                ? createPortal(
                      <NoteAIEditModal
                          applyMode={aiEditPresetId === "continue" ? "append" : "replace"}
                          errorMessage={aiEditErrorMessage}
                          isStreaming={isAIEditStreaming}
                          prompt={aiEditPrompt}
                          previewMode={aiEditPreviewMode}
                          responseText={aiEditResponseText}
                          selectedPresetId={aiEditPresetId}
                          selection={aiEditSession.selection}
                          status={aiEditStatus}
                          onApply={previewAIEditResult}
                          onClose={closeAIEditModal}
                          onConfirmApply={confirmAIEditApply}
                          onPromptChange={setAIEditPrompt}
                          onPreviewBack={() => setAIEditPreviewMode(false)}
                          onRequest={handleAIEditRequest}
                          onSelectPreset={handleAIEditPresetSelect}
                          onStop={stopAIEditRequest}
                      />,
                      document.body,
                  )
                : null}
        </PageLayout>
    );
}

export default NoteDetailPage;
